import { OfferType } from '@/types';
import { MAX_FILTERS_RANGE_VALUES } from '@/constants/maxFilterRangeValues';
import { WLAS_PARAMS } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/constants';
import {
  WlasFilters,
  WlasValues,
  defaultFilters,
  defaultValues,
} from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/utils';

/** Body of GET/PUT /private/broker_admin/wlas_config. */
export interface WlasApiFilters {
  offerType?: OfferType | null;
  priceFrom?: number | null;
  priceTo?: number | null;
  rentPriceFrom?: number | null;
  rentPriceTo?: number | null;
  spaceFrom?: number | null;
  spaceTo?: number | null;
  roomNumberFrom?: number | null;
  roomNumberTo?: number | null;
  categories?: number[] | null;
  countries?: string[] | null;
  division?: string[] | null;
  districts?: string[] | null;
  customEquips?: string[] | null;
  powerConsumptionClasses?: string[] | null;
  energyClassFactors?: string[] | null;
  excludeOfficeIds?: number[] | null;
}

export interface WlasApiConfig {
  memberUniqueCode: string;
  params: WlasValues | null;
  filters: WlasApiFilters | null;
}

const EXCLUDE_PARAM = 'exclude-office-id';

function bound(value: number | null | undefined, fallback: number): number {
  return value == null ? fallback : value;
}

function rangeToApi(
  range: number[] | undefined,
  maxConstant: number,
): { from: number | null; to: number | null } {
  const min = range?.[0] ?? 0;
  const max = range?.[1] ?? maxConstant;
  return {
    from: min === 0 ? null : min,
    to: max >= maxConstant ? null : max,
  };
}

function splitLocations(codes: string[]) {
  const countries: string[] = [];
  const division: string[] = [];
  const districts: string[] = [];
  for (const code of codes) {
    const parts = code.split('.').length;
    if (parts <= 1) countries.push(code);
    else if (parts === 2) division.push(code);
    else districts.push(code);
  }
  return {
    countries: countries.length ? countries : null,
    division: division.length ? division : null,
    districts: districts.length ? districts : null,
  };
}

function parseOfficeIds(raw: string | boolean | undefined): number[] | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const ids = raw
    .split(/[\s,]+/)
    .map((part) => Number(part))
    .filter((id) => Number.isFinite(id));
  return ids.length ? ids : null;
}

export function toApiParams(values: WlasValues): WlasValues {
  const params = { ...values };
  delete params[EXCLUDE_PARAM];
  return params;
}

export function toApiFilters(filters: WlasFilters, values: WlasValues): WlasApiFilters | null {
  const price = rangeToApi(filters.priceValue, MAX_FILTERS_RANGE_VALUES.SELL_PRICE);
  const rent = rangeToApi(filters.rentPriceValue, MAX_FILTERS_RANGE_VALUES.RENT_PRICE);
  const area = rangeToApi(filters.areaValue, MAX_FILTERS_RANGE_VALUES.AREA);
  const rooms = rangeToApi(filters.roomsValue, MAX_FILTERS_RANGE_VALUES.ROOMS);
  const locations = splitLocations(filters.unitListValue ?? []);

  // null = no restriction (all selected); explicit array = restriction to those items
  const categories =
    filters.categories != null
      ? filters.categories.map((code) => Number(code)).filter((id) => Number.isFinite(id))
      : null;

  const offerType =
    filters.offerTypes != null && filters.offerTypes.length === 1 ? filters.offerTypes[0] : null;

  const api: WlasApiFilters = {
    offerType,
    priceFrom: price.from,
    priceTo: price.to,
    rentPriceFrom: rent.from,
    rentPriceTo: rent.to,
    spaceFrom: area.from,
    spaceTo: area.to,
    roomNumberFrom: rooms.from,
    roomNumberTo: rooms.to,
    categories: categories != null && categories.length ? categories : null,
    countries: filters.unitListValue != null ? locations.countries : null,
    division: filters.unitListValue != null ? locations.division : null,
    districts: filters.unitListValue != null ? locations.districts : null,
    customEquips: filters.equipment?.length ? filters.equipment : null,
    powerConsumptionClasses: filters.powerConsumptionClasses?.length
      ? filters.powerConsumptionClasses
      : null,
    energyClassFactors: filters.energyClassFactors?.length ? filters.energyClassFactors : null,
    excludeOfficeIds: parseOfficeIds(values[EXCLUDE_PARAM]),
  };

  const hasValue = Object.values(api).some((value) => value != null);
  return hasValue ? api : null;
}

export function fromApiConfig(api: WlasApiConfig): {
  memberUniqueCode: string;
  params: WlasValues;
  filters: WlasFilters;
} {
  const filters = api.filters ?? {};
  const defaults = defaultFilters();

  const locationCodes = [
    ...(filters.countries ?? []),
    ...(filters.division ?? []),
    ...(filters.districts ?? []),
  ];
  const hasLocationFilter =
    filters.countries != null || filters.division != null || filters.districts != null;

  const uiFilters: WlasFilters = {
    offerTypes: filters.offerType ? [filters.offerType] : null,
    categories: filters.categories != null ? filters.categories.map(String) : null,
    unitListValue: hasLocationFilter ? locationCodes : null,
    priceValue: [bound(filters.priceFrom, 0), bound(filters.priceTo, defaults.priceValue[1])],
    rentPriceValue: [
      bound(filters.rentPriceFrom, 0),
      bound(filters.rentPriceTo, defaults.rentPriceValue[1]),
    ],
    areaValue: [bound(filters.spaceFrom, 0), bound(filters.spaceTo, defaults.areaValue[1])],
    roomsValue: [
      bound(filters.roomNumberFrom, 0),
      bound(filters.roomNumberTo, defaults.roomsValue[1]),
    ],
    equipment: filters.customEquips ?? null,
    powerConsumptionClasses: filters.powerConsumptionClasses ?? null,
    energyClassFactors: filters.energyClassFactors ?? null,
  };

  const params: WlasValues = { ...defaultValues(), ...(api.params ?? {}) };

  // Sanitize select params: if the saved value is not among the defined options, fall back to defaultVal.
  for (const p of WLAS_PARAMS) {
    if (p.type === 'select' && p.options) {
      const val = String(params[p.id] ?? '');
      if (!p.options.some((opt) => opt.value === val)) {
        params[p.id] = p.defaultVal;
      }
    }
  }

  delete params[EXCLUDE_PARAM];
  params[EXCLUDE_PARAM] = (filters.excludeOfficeIds ?? []).join(',');

  return {
    memberUniqueCode: api.memberUniqueCode,
    params,
    filters: uiFilters,
  };
}
